import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import getApiUrl from "../utils/api";

export const NotificationsContext = createContext({
  notifications: [],
  unreadCount: 0,
  modalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  addNotification: () => {},
  removeNotification: () => {},
  markAllRead: () => {},
  clearAll: () => {},
});

const STORAGE_KEY_BASE = "relay.notifications.v1";
const keyForUser = (userId) => `${STORAGE_KEY_BASE}.${userId || "anon"}`;

export default function NotificationsProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const u = JSON.parse(stored);
        return u.userId || u.id || u.sub || null;
      }
    } catch {}
    return null;
  });

  useEffect(() => {
    if (currentUserId) return;
    try {
      fetch(getApiUrl("api/auth/me"), { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) =>
          setCurrentUserId(
            d?.user?.userId || d?.user?.id || d?.user?.sub || null
          )
        )
        .catch(() => {});
    } catch {}
  }, [currentUserId]);

  // Load notifications for this user when we know their id
  useEffect(() => {
    if (!currentUserId) return;
    try {
      const legacy = localStorage.getItem(STORAGE_KEY_BASE);
      const userKey = keyForUser(currentUserId);
      const existing = localStorage.getItem(userKey);
      const raw = existing || legacy;
      setNotifications(raw ? JSON.parse(raw) : []);
    } catch {
      setNotifications([]);
    }
  }, [currentUserId]);

  // Fetch from server as source of truth once user is known
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    const fetchServer = async () => {
      try {
        const res = await fetch(getApiUrl("api/notifications"), {
          credentials: "include",
        });
        if (!res.ok) return;
        const items = await res.json();
        if (cancelled) return;
        // Normalize createdAt/meta keys
        const mapped = (items || []).map((n) => {
          let metadata;
          try {
            metadata = n.metadata ? JSON.parse(n.metadata) : {};
          } catch {
            metadata = {};
          }
          return {
            id: n.id,
            type: n.type,
            title: n.title,
            message: n.message,
            createdAt: n.created_at || n.createdAt || new Date().toISOString(),
            read: !!n.is_read,
            meta: metadata,
          };
        });
        setNotifications((prev) => {
          // Merge by id; server wins
          const byId = new Map(prev.map((p) => [String(p.id), p]));
          mapped.forEach((m) => byId.set(String(m.id), m));
          return Array.from(byId.values()).sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
        });
      } catch {}
    };
    fetchServer();
    return () => {
      cancelled = true;
    };
  }, [currentUserId]);

  // Lightweight polling to refresh notifications from server

  useEffect(() => {
    try {
      if (!currentUserId) return;
      localStorage.setItem(
        keyForUser(currentUserId),
        JSON.stringify(notifications)
      );
    } catch {}
  }, [notifications, currentUserId]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const addNotification = useCallback(
    async (notification) => {
      // Only show to the intended user if target specified
      const targetId =
        notification.targetUserId ||
        notification.meta?.assignedToId ||
        notification.meta?.assigneeId;
      if (targetId && !currentUserId) {
        // Current user unknown yet; avoid showing mis-targeted items
        return;
      }
      if (
        targetId &&
        currentUserId &&
        String(targetId) !== String(currentUserId)
      ) {
        return; // Not for this user
      }
      // Try server persistence
      let created = null;
      try {
        const res = await fetch(getApiUrl("api/notifications"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            targetUserId: notification.targetUserId,
            type: notification.type,
            title: notification.title,
            message: notification.message,
            meta: notification.meta,
          }),
        });
        if (res.ok) {
          created = await res.json();
        }
      } catch {}

      const localId =
        created?.id ||
        notification.id ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const createdAt =
        created?.created_at ||
        notification.createdAt ||
        new Date().toISOString();
      setNotifications((prev) => [
        {
          id: localId,
          type: notification.type || created?.type || "info",
          title: notification.title || created?.title || "Notification",
          message: notification.message || created?.message || "",
          createdAt,
          read: false,
          meta: notification.meta || created?.meta || {},
        },
        ...prev,
      ]);
    },
    [currentUserId]
  );

  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(getApiUrl("api/notifications"), {
          credentials: "include",
        });
        if (!res.ok) return;
        const items = await res.json();
        if (cancelled) return;
        const mapped = (items || []).map((n) => {
          let metadata;
          try {
            metadata = n.metadata ? JSON.parse(n.metadata) : {};
          } catch {
            metadata = {};
          }
          return {
            id: n.id,
            type: n.type,
            title: n.title,
            message: n.message,
            createdAt: n.created_at || n.createdAt || new Date().toISOString(),
            read: !!n.is_read,
            meta: metadata,
          };
        });
        setNotifications((prev) => {
          const byId = new Map(prev.map((p) => [String(p.id), p]));
          mapped.forEach((m) => byId.set(String(m.id), m));
          return Array.from(byId.values()).sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
        });
      } catch {}
    };
    poll();
    const id = setInterval(poll, 20000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [currentUserId]);

  const removeNotification = useCallback(async (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    try {
      await fetch(getApiUrl(`api/notifications/${id}`), {
        method: "DELETE",
        credentials: "include",
      });
    } catch {}
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await fetch(getApiUrl("api/notifications/mark-all-read"), {
        method: "POST",
        credentials: "include",
      });
    } catch {}
  }, []);

  const clearAll = useCallback(async () => {
    setNotifications([]);
    try {
      await fetch(getApiUrl("api/notifications"), {
        method: "DELETE",
        credentials: "include",
      });
    } catch {}
  }, []);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      modalOpen,
      openModal,
      closeModal,
      addNotification,
      removeNotification,
      markAllRead,
      clearAll,
    }),
    [
      notifications,
      unreadCount,
      modalOpen,
      openModal,
      closeModal,
      addNotification,
      removeNotification,
      markAllRead,
      clearAll,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}
